import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../lib/cn';

/**
 * Form Field — label + control + hint + error compose.
 * Tek bir id ile aria-describedby + aria-invalid bağlanır. RHF ile uyumlu
 * (control'ün ref'ini iletmek için forwardRef'li primitive'leri kullan).
 *
 * Kullanım:
 *   <Field label="Email" hint="..." error={errors.email?.message}>
 *     <Input type="email" {...register('email')} />
 *   </Field>
 */

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  /** Eğer custom id istiyorsan; aksi halde otomatik üretilir. */
  htmlFor?: string;
}

export function Field({ label, hint, error, required, children, className, htmlFor }: FieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  // Tek child + valid React element ise id + aria-* prop'larını enrich et.
  const enriched = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: ((children as ReactElement<Record<string, unknown>>).props.id as string) ?? id,
        'aria-describedby':
          describedBy ??
          ((children as ReactElement<Record<string, unknown>>).props['aria-describedby'] as string | undefined),
        'aria-invalid':
          !!error || ((children as ReactElement<Record<string, unknown>>).props['aria-invalid'] as boolean | undefined),
      })
    : children;

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="ml-0.5 text-custel-danger">*</span>}
        </Label>
      )}
      {enriched}
      {hint && !error && (
        <p id={hintId} className="text-xs text-custel-text-secondary">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-custel-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ---- Label ---- (BRANDBOOK §3.2 .label — JBM 12px uppercase)
export const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...rest }, ref) => (
    <label
      ref={ref}
      className={cn('block font-mono text-[12px] font-medium uppercase tracking-[0.04em] text-custel-g700', className)}
      {...rest}
    />
  ),
);
Label.displayName = 'Label';

// ---- Input ---- (BRANDBOOK §6.3 — 8C Filled + Lime Focus Glow)
//   - h-40 default · bg g100 · radius 6 · hover #e8e5d8 · focus paper + lime ring
//   - variant=mono → JetBrains Mono (IBAN/VKN/TCKN/ETTN)
const inputBase =
  'w-full rounded-custel-md border border-transparent bg-custel-g100 px-3 text-sm text-custel-ink placeholder:text-custel-text-tertiary transition-all duration-custel-fast hover:bg-custel-g150 focus:bg-custel-paper focus:border-custel-border-strong focus:outline-none focus:ring-[3px] focus:ring-custel-lime/40 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-custel-danger aria-[invalid=true]:bg-custel-danger-subtle aria-[invalid=true]:focus:ring-custel-danger/20';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  inputSize?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'mono';
}

const sizeClass = (s: InputProps['inputSize']) =>
  s === 'sm' ? 'h-8 text-[13px]' : s === 'lg' ? 'h-12 text-[15px]' : 'h-10 text-sm';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, leadingIcon, trailingIcon, inputSize = 'md', variant = 'default', ...rest }, ref) => {
    const variantClass = variant === 'mono' ? 'font-mono tracking-tight' : '';
    const merged = cn(inputBase, sizeClass(inputSize), variantClass);

    if (!leadingIcon && !trailingIcon) {
      return <input ref={ref} className={cn(merged, className)} {...rest} />;
    }
    return (
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-custel-text-tertiary">
            {leadingIcon}
          </span>
        )}
        <input ref={ref} className={cn(merged, leadingIcon && 'pl-9', trailingIcon && 'pr-9', className)} {...rest} />
        {trailingIcon && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-custel-text-tertiary">
            {trailingIcon}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

// ---- Textarea ---- (filled, lime glow)
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'w-full resize-y rounded-custel-md border border-transparent bg-custel-g100 px-3 py-2 text-sm text-custel-ink placeholder:text-custel-text-tertiary transition-all duration-custel-fast hover:bg-custel-g150 focus:bg-custel-paper focus:border-custel-border-strong focus:outline-none focus:ring-[3px] focus:ring-custel-lime/40 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-custel-danger aria-[invalid=true]:bg-custel-danger-subtle',
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

// ---- Select (native) ----
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...rest }, ref) => (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full appearance-none rounded-custel-md border border-transparent bg-custel-g100 bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat px-3 pr-8 text-sm text-custel-ink transition-all duration-custel-fast hover:bg-custel-g150 focus:bg-custel-paper focus:border-custel-border-strong focus:outline-none focus:ring-[3px] focus:ring-custel-lime/40 disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236E6A5E' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E\")",
      }}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

// ---- Toggle (switch) ----
interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  id?: string;
}

export function Toggle({ checked, onChange, disabled, label, description, id }: ToggleProps) {
  const generatedId = useId();
  const switchId = id ?? generatedId;
  return (
    <label htmlFor={switchId} className={cn('flex items-start gap-3', disabled && 'opacity-60')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        id={switchId}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink disabled:cursor-not-allowed',
          // BRANDBOOK §2.5 lime kuralı: toggle on-state ink, off-state g300.
          // Lime burada kullanılırsa "her toggle vurgu" hatasına düşeriz.
          checked ? 'bg-custel-ink' : 'bg-custel-g300',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-custel-paper shadow-custel-sm transition-transform',
            checked ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
          )}
        />
      </button>
      {(label || description) && (
        <div className="text-sm">
          {label && <p className="font-medium text-custel-ink">{label}</p>}
          {description && <p className="text-xs text-custel-text-secondary">{description}</p>}
        </div>
      )}
    </label>
  );
}
